import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ImageItem } from "@/types/minds-eye";
import { CheckCircle2 } from "lucide-react";

interface ImageSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  images: ImageItem[];
  onStartCycle: (selectedImages: ImageItem[]) => void;
}

const ImageSelectionModal: React.FC<ImageSelectionModalProps> = ({
  isOpen,
  onClose,
  images,
  onStartCycle
}) => {
  const [selectedImageIds, setSelectedImageIds] = useState<string[]>([]);

  // Reset selections when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedImageIds([]);
    }
  }, [isOpen]);

  const handleToggleImage = (imageId: string) => {
    setSelectedImageIds(prev => 
      prev.includes(imageId)
        ? prev.filter(id => id !== imageId)
        : [...prev, imageId]
    );
  };

  const handleStartCycle = () => {
    const selectedImages = images.filter(img => selectedImageIds.includes(img.id));
    onStartCycle(selectedImages);
  };

  // Group images by folder
  const gratefulImages = images.filter(img => img.folder === 'grateful');
  const aspiringImages = images.filter(img => img.folder === 'aspiring');

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="cosmic-card max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-gradient">
            Select Images for Mind's Eye Cycle
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-4">
          <p className="text-white/80 mb-4">
            Select images from your galleries to include in your Mind's Eye Cycle. 
            You'll reflect on each with gratitude during the cycle.
          </p>
          
          {/* No images message */}
          {images.length === 0 && (
            <div className="text-center py-8">
              <p className="text-white/60">
                You don't have any images yet. Upload some images first to start a Mind's Eye Cycle.
              </p>
            </div>
          )}
          
          {/* Grateful For section */}
          {gratefulImages.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium mb-3 text-cosmic-300">Grateful For</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {gratefulImages.map(image => (
                  <div key={image.id} className="space-y-2">
                    <div className="relative">
                      {image.type === "video" ? (
                        <video 
                          src={image.url} 
                          muted 
                          autoPlay 
                          loop 
                          playsInline
                          className={`w-full h-24 object-cover rounded-md transition-opacity ${
                            selectedImageIds.includes(image.id) ? 'ring-2 ring-cosmic-500' : ''
                          }`}
                        />
                      ) : (
                        <img 
                          src={image.url} 
                          alt={image.name} 
                          className={`w-full h-24 object-cover rounded-md transition-opacity ${
                            selectedImageIds.includes(image.id) ? 'ring-2 ring-cosmic-500' : ''
                          }`}
                        />
                      )}
                      <div className="absolute top-2 right-2">
                        <Checkbox 
                          id={`image-${image.id}`}
                          checked={selectedImageIds.includes(image.id)}
                          onCheckedChange={() => handleToggleImage(image.id)}
                          className="bg-black/50 data-[state=checked]:bg-cosmic-500 data-[state=checked]:text-white"
                        />
                      </div>
                    </div>
                    <Label 
                      htmlFor={`image-${image.id}`}
                      className="text-xs text-white/80 cursor-pointer"
                    >
                      {image.name.length > 20 ? `${image.name.substring(0, 20)}...` : image.name}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Aspiring To section */}
          {aspiringImages.length > 0 && (
            <div>
              <h3 className="text-lg font-medium mb-3 text-cosmic-300">Aspiring To</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {aspiringImages.map(image => (
                  <div key={image.id} className="space-y-2">
                    <div className="relative">
                      {image.type === "video" ? (
                        <video 
                          src={image.url} 
                          muted 
                          autoPlay 
                          loop 
                          playsInline
                          className={`w-full h-24 object-cover rounded-md transition-opacity ${
                            selectedImageIds.includes(image.id) ? 'ring-2 ring-cosmic-500' : ''
                          }`}
                        />
                      ) : (
                        <img 
                          src={image.url} 
                          alt={image.name} 
                          className={`w-full h-24 object-cover rounded-md transition-opacity ${
                            selectedImageIds.includes(image.id) ? 'ring-2 ring-cosmic-500' : ''
                          }`}
                        />
                      )}
                      <div className="absolute top-2 right-2">
                        <Checkbox 
                          id={`image-${image.id}`}
                          checked={selectedImageIds.includes(image.id)}
                          onCheckedChange={() => handleToggleImage(image.id)}
                          className="bg-black/50 data-[state=checked]:bg-cosmic-500 data-[state=checked]:text-white"
                        />
                      </div>
                      {image.isAIGenerated && (
                        <div className="absolute top-2 left-2 bg-cosmic-500/80 text-white text-xs px-1 rounded">
                          AI
                        </div>
                      )}
                    </div>
                    <Label 
                      htmlFor={`image-${image.id}`}
                      className="text-xs text-white/80 cursor-pointer"
                    >
                      {image.name.length > 20 ? `${image.name.substring(0, 20)}...` : image.name}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <DialogFooter className="flex justify-between">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            className="cosmic-button" 
            onClick={handleStartCycle}
            disabled={selectedImageIds.length === 0}
          >
            <CheckCircle2 className="mr-2 h-4 w-4" />
            Start Cycle with {selectedImageIds.length} {selectedImageIds.length === 1 ? 'Image' : 'Images'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImageSelectionModal;
